// xpSystem.js — système XP et niveaux Calestan
// Coef 1.05, base 100 XP, max niveau 50

// ─── CONFIGURATION ────────────────────────────────────────────────────────────
const XP_BASE       = 100;
const XP_COEF       = 1.05;
const MAX_LEVEL     = 50;

// XP requis pour passer du niveau N au niveau N+1
export function xpForLevel(level) {
  return Math.round(XP_BASE * Math.pow(XP_COEF, level - 1));
}

// XP total cumulé pour atteindre le début du niveau N
export function xpThresholdForLevel(level) {
  if (level <= 1) return 0;
  let total = 0;
  for (let i = 1; i < level; i++) total += xpForLevel(i);
  return total;
}

// Niveau actuel depuis XP total
export function getLevelFromXP(totalXP) {
  let level = 1;
  while (level < MAX_LEVEL && totalXP >= xpThresholdForLevel(level + 1)) {
    level++;
  }
  return level;
}

// XP dans le niveau actuel et XP requis pour le prochain
export function getLevelProgress(totalXP) {
  const level    = getLevelFromXP(totalXP);
  const current  = xpThresholdForLevel(level);
  const next     = level < MAX_LEVEL ? xpThresholdForLevel(level + 1) : null;
  const xpInLevel   = totalXP - current;
  const xpNeeded    = next ? next - current : null;
  const pct         = next ? Math.min(xpInLevel / xpNeeded, 1) : 1;
  return { level, xpInLevel, xpNeeded, pct, isMaxLevel: level >= MAX_LEVEL };
}

// ─── CALCUL XP PAR ÉVÉNEMENT ─────────────────────────────────────────────────

// XP pour une séance muscu terminée
// session : { exercises, date }
// prevSessions : séances précédentes (pour détecter le streak 48h et la semaine complète)
// weekTarget : nb de jours cible par semaine (profile.days)
export function calcSessionXP(session, prevSessions = [], weekTarget = 3) {
  let xp = 20; // base muscu

  // +1 XP par exercice complété (au moins 1 série avec reps > 0)
  const completedEx = (session.exercises || []).filter(ex =>
    (ex.sets || []).some(s => (s.reps || 0) > 0)
  ).length;
  xp += completedEx;

  // Bonus streak 48h : dernière séance dans les 48h
  if (prevSessions.length > 0) {
    const sorted = [...prevSessions].sort((a, b) => new Date(b.date) - new Date(a.date));
    const last = sorted[0];
    if (last?.date) {
      const diffH = (new Date(session.date) - new Date(last.date)) / 3600000;
      if (diffH > 0 && diffH <= 48) xp += 5;
    }
  }

  // Bonus semaine complète : si cette séance complète la semaine
  if (weekTarget > 0) {
    const sessionDate = new Date(session.date);
    const dayOfWeek = sessionDate.getDay() === 0 ? 6 : sessionDate.getDay() - 1;
    const weekStart = new Date(sessionDate);
    weekStart.setDate(sessionDate.getDate() - dayOfWeek);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);

    const daysThisWeek = new Set(
      prevSessions
        .filter(s => {
          const d = new Date(s.date);
          return d >= weekStart && d < weekEnd;
        })
        .map(s => new Date(s.date).toDateString())
    );

    // Si cette séance complète le quota
    if (daysThisWeek.size + 1 >= weekTarget) xp += 15;
  }

  return xp;
}

// XP pour une séance tabata
export function calcTabataXP() {
  return 15;
}

// XP pour un PR enregistré
export function calcPRXP() {
  return 2;
}

// ─── XP TOTAL RECALCULÉ DEPUIS L'HISTORIQUE ──────────────────────────────────
// Utilisé pour la migration : recalcule l'XP depuis toutes les sessions existantes
export function recalcTotalXP(sessions = [], prs = {}, weekTarget = 3) {
  let total = 0;

  const sorted = [...sessions].sort((a, b) => new Date(a.date) - new Date(b.date));

  sorted.forEach((s, i) => {
    const prev = sorted.slice(0, i);
    if (s.type === "tabata") {
      total += calcTabataXP();
    } else {
      total += calcSessionXP(s, prev, weekTarget);
    }
  });

  // PR existants : +2 XP chacun
  total += Object.keys(prs).length * calcPRXP();

  return total;
}

// ─── MÉDAILLES PAR NIVEAU ─────────────────────────────────────────────────────
// Médailles débloquées à certains niveaux (utilisé dans ProfilePage)
export const MEDAL_LEVELS = {
  bronze:    10,
  argent:    20,
  or:        30,
  diamant:   40,
  collector: 50,
};
